<script setup>
import Table from "@/components/utils/Table.vue";
import { formatSchemePlanApplicationStage } from "./scheme_plan_application_business_logic";

const props = defineProps({
  applications: {
    type: Array,
    default: () => [],
  },
  applicationsNotEmpty: {
    type: Boolean,
    default: false,
  },
  loadingApplicationId: {
    type: [Number, String],
    default: null,
  },
  loadingContinueId: {
    type: [Number, String],
    default: null,
  },
});

const emit = defineEmits(["viewApplicationClicked", "payBillClicked", "resolveQueryClicked", "continueClicked"]);

function formatDate(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function getStatusColor(status) {
  const statusLower = (status || "").toLowerCase();
  if (statusLower === "approved") return "success";
  if (statusLower === "rejected") return "error";
  if (statusLower === "pending") return "warning";
  if (statusLower === "deferred") return "deep-orange";
  if (statusLower === "in_progress") return "info";
  if (statusLower === "draft") return "blue-grey";
  return "grey";
}
</script>

<template>
  <Table
    :headers="[
      { text: 'Application No.', value: 'application_number' },
      { text: 'PN', value: 'pn' },
      { text: 'LR Number', value: 'lr_number' },
      { text: 'Owner(s)', value: 'owner' },
      { text: 'Category', value: 'category' },
      { text: 'Type', value: 'type' },
      { text: 'Status', value: 'status' },
      { text: 'Stage', value: 'stage' },
      { text: 'Date', value: 'created_at' },
      { text: 'Actions', value: 'view', action: true },
    ]"
    :items="applications"
    emptyText="No applications found."
    headClasses="primary white--text"
  >
    <template #item.pn="{ item }">
      {{ item.scheme_plan_property?.pn || "-" }}
    </template>

    <template #item.lr_number="{ item }">
      {{ item.scheme_plan_property?.lr_number || "-" }}
    </template>

    <template #item.owner="{ item }">
      {{ item.client?.customer_name || "-" }}
    </template>

    <template #item.category="{ item }">
      {{ item.scheme_plan_category?.name || "-" }}
    </template>

    <template #item.type="{ item }">
      {{ item.scheme_plan_type?.name || "-" }}
    </template>

    <template #item.status="{ item }">
      <v-chip small :color="getStatusColor(item.status)" dark>
        {{ item.status }}
      </v-chip>
    </template>

    <template #item.stage="{ item }">
      {{ formatSchemePlanApplicationStage(item.stage) }}
    </template>

    <template #item.created_at="{ item }">
      {{ formatDate(item.created_at) }}
    </template>

    <template #item.view="{ item }">
      <div class="d-flex align-center">
        <v-tooltip bottom v-if="item.bill && item.bill.work_flow_status?.code !== 'PYD'">
          <template #activator="{ on }">
            <v-btn icon color="success" v-on="on" @click="$emit('payBillClicked', item)">
              <v-icon small>mdi-cash</v-icon>
            </v-btn>
          </template>
          <span>Pay Bill</span>
        </v-tooltip>

        <v-tooltip bottom v-if="(item.status || '').toLowerCase() === 'draft'">
          <template #activator="{ on }">
            <v-btn
              icon
              color="blue-grey darken-1"
              v-on="on"
              :loading="loadingContinueId === item.id"
              :disabled="loadingContinueId !== null || loadingApplicationId !== null"
              @click="$emit('continueClicked', item)"
            >
              <v-icon small>mdi-pencil-circle</v-icon>
            </v-btn>
          </template>
          <span>Continue Draft</span>
        </v-tooltip>

        <v-tooltip bottom v-if="(item.status || '').toLowerCase() === 'deferred'">
          <template #activator="{ on }">
            <v-btn icon color="warning" v-on="on" @click="$emit('resolveQueryClicked', item)">
              <v-icon small>mdi-comment-alert</v-icon>
            </v-btn>
          </template>
          <span>Resolve Query</span>
        </v-tooltip>

        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn
              icon
              color="primary"
              v-on="on"
              :loading="loadingApplicationId === item.id"
              :disabled="loadingApplicationId !== null || loadingContinueId !== null"
              @click="$emit('viewApplicationClicked', item)"
            >
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
          </template>
          <span>View Details</span>
        </v-tooltip>
      </div>
    </template>
  </Table>
</template>

